import { PortfolioType } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { BaseHolding, PortfolioChild, PortfolioReferenceBuilder, Security, SecurityDirector, SubstitutedFrom, TreeNodeMap, WebUserDataConstants } from '@ddt-web/domain';
import { ModelNameUtil } from '@ddt-web/generic-utils';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import * as cloneDeep from 'lodash/cloneDeep';
import * as compact from 'lodash/compact';
import * as every from 'lodash/every';
import * as get from 'lodash/get';
import * as has from 'lodash/has';
import * as isEmpty from 'lodash/isEmpty';
import * as uniq from 'lodash/uniq';

import { BasicPortfolioAttributes } from '../models/basic-portfolio-attributes.interface';
import { BlendPortfolioUtil } from './blend-portfolio.util';

/**
 * Logic used in the portfolio-editor-blending reducers
 */

export class PortfolioEditorViewTreeReducerUtil {

	@InjectConfig(SharedConfigKey.SHOULD_USE_DAS_PORTFOLIO_ID) private static readonly shouldUseDasPortfolioId = true;

	/**
	* Adds a security to a PortfolioChild in the tree		
	*/
	public static addSecurityToPortfolioTreeMap(portfolioChildMap: TreeNodeMap, securities: BaseHolding[], parent: string, compareAladdinId: boolean): TreeNodeMap {

		const currentParentChild: PortfolioChild = portfolioChildMap[parent];
		let newChildrenRefs = [...currentParentChild.childrenRef];

		if (currentParentChild && currentParentChild.childrenRef) {
			securities.forEach(security => {
				const childExistsInPortfolio = compareAladdinId // this is false in AC and SMA tickers are null
					? currentParentChild.childrenRef.find(ref => ref.aladdinId === security.aladdinId)
					: currentParentChild.childrenRef.find(ref => security.ticker && (ref.ticker === security.ticker));

				// if security does not exist in portfolio
				if (!childExistsInPortfolio) {
					newChildrenRefs = [security, ...newChildrenRefs];
					// if security is existing but the incoming security has defined weights/values, sum them
				} else if (currentParentChild.childrenRef.find(ref => ref.identifier === security.identifier && (security.weight || security.value))) {
					const indexOfOldSecurity = currentParentChild.childrenRef.findIndex((ref) => ref.identifier === security.identifier);
					const updatedSecurity = cloneDeep(newChildrenRefs[indexOfOldSecurity]);
					updatedSecurity.weight = (updatedSecurity.weight && security.weight) ? updatedSecurity.weight + security.weight : security.weight;
					updatedSecurity.value = (updatedSecurity.value && security.value) ? updatedSecurity.value + security.value : security.value;

					// if security already exists in children refs, but the incoming security has substitutedFrom, update the object
					this.updateSubstituteFromObject(security, updatedSecurity);

					// immutably replacing old security in the array
					newChildrenRefs = Object.assign([...newChildrenRefs], { [indexOfOldSecurity]: updatedSecurity });
				}
			});
		}

		const updatedParentChild = {
			...currentParentChild,
			childrenRef: newChildrenRefs
		} as PortfolioChild;

		return {
			...portfolioChildMap,
			[parent]: updatedParentChild
		};
	}

	public static buildPortfolioChildNode(
		portfolio: any,
		parent: string,
		portfolioChildMap: TreeNodeMap,
		modelsConfig: Array<any>,
		isTopNode = false,
		portfolioAttributes?: Array<BasicPortfolioAttributes>
	): TreeNodeMap {

		let node: PortfolioChild;
		let portfolioAttribute: BasicPortfolioAttributes;

		const portfolioData = get(portfolio, 'portfolio', portfolio);

		let portfolioDataId = get(portfolioData, 'id') || get(portfolioData, 'uniqueId');

		// we need to add translation to load newly created blend in old infra, TODO wdsDecom: can be removed once all tools are fully migrated on new infra & Fallback to id when analyzing a model directly from PE 
		if (PortfolioIdUtil.isBlackRockModel(portfolioDataId)) {
			portfolioDataId = this.shouldUseDasPortfolioId ? get(portfolioData, 'dasId') || get(portfolioData, 'allData.dasId') ||
				get(portfolioData, 'allData.requestModelId') || get(portfolioData, 'id') : portfolioDataId;
		}

		if (!isEmpty(portfolioAttributes)) {
			portfolioAttribute = portfolioAttributes.find(item => portfolioDataId === item.uniqueId);
		}

		if (portfolioData.children && portfolioData.children.length) {

			// * 0 If we are at the top node of the tree returned and we want to add a reference to an existing PortfolioChild in the store, add reference to node
			if (isTopNode) {
				portfolioChildMap = this.addRefToParent(portfolioChildMap,
					parent,
					portfolioDataId,
					portfolioData.type,
					portfolioData.subType,
					get(portfolioData, 'allData.baseModel'),
					PortfolioEditorViewTreeReducerUtil.mapDescription(portfolioData, modelsConfig),
					portfolioData.aladdinPortfolioCode,
					portfolioData.tpmId,
					portfolioAttribute
				);
			}

			// * 1: Get array of childrenRef
			const childRefArray = portfolioData.children.map(child => {
				// Holding has error field
				const hasErrors = Boolean(get(child, 'error'));

				if (has(child, 'allData.positionType')) {
					// If it's from iRetire server for partner
					const tickerIdentifier = get(child, 'allData.otherIdentifiers', []).find(identifier => identifier.identifierType === 'TICKER');
					const ticker = get(tickerIdentifier, 'identifier') || get(child, 'identifier') || '';
					const description = get(child, 'portfolio.description') || get(child, 'allData.originPlatform.name', child.description);
					const type = get(child, 'portfolio.type');
					const weight = get(child, 'weight'); // make sure we persist the weight from the child level and not the one in allData (allData is not updated when fixing sleeve and updating weight)
					const security = Object.assign({ ticker, description, type }, child, { ...child.allData, weight });
					return security;
				} else if (
					BlendPortfolioUtil.isUserPortfolio(child) || BlendPortfolioUtil.isAPMSleeve(child) ||
					(child.portfolio && (child.portfolio.type === WebUserDataConstants.TYPE.PORTFOLIO || BlendPortfolioUtil.isModel(child.portfolio)))
				) {
					const originPlatform = get(child, 'allData.originPlatform', child.originPlatform);
					const otherIdentifiers = get(child, 'allData.otherIdentifiers', child.otherIdentifiers);
					const flags = get(child, 'portfolio.allData.flags', child.flags);

					// before the flipped store this used to look by the new APC as key
					// this search won't work anymore. also I think if ACServer gives back dasId and child.portfolio.id then we don't need to do this 
					// const model = modelsConfig.find(port => child.portfolio?.id === port.id);

					// we need to add translation of model to use new identifier for model only
					let identifier: string = '';
					let aladdinPortfolioCode;

					if (BlendPortfolioUtil.isBlackRockModel(child.portfolio)) {
						// we need to add translation to load newly created blend in old infra, TODO wdsDecom: can be removed once all tools are fully migrated on new infra & Fallback to id when analyzing a model directly from PE 
						identifier = this.shouldUseDasPortfolioId ? child.portfolio?.dasId || get(child.portfolio, 'allData.dasId') ||
							get(child.portfolio, 'allData.requestModelId') || get(child.portfolio, 'id') : child.portfolio.id;
						aladdinPortfolioCode = child.allData?.aladdinPortfolioCode || child.portfolio?.aladdinPortfolioCode;
					} else {
						identifier = child.identifier;
					}

					return new PortfolioReferenceBuilder()
						.setIdentifier(identifier)
						.setIdentifierType(child.identifierType)
						.setAladdinPortfolioCode(aladdinPortfolioCode)
						.setDescription(PortfolioEditorViewTreeReducerUtil.mapDescription(child, modelsConfig))
						.setSubType(get(child, 'subType', 'portfolio.subType'))
						.setType(get(child, 'type', 'portfolio.type'))
						.setOriginPlatform(originPlatform) // needs to be persisted for apm sleeves
						.setOtherIdentifiers(otherIdentifiers) // needs to be persisted for apm sleeves
						.setBaseModel(child.baseModel)
						.setWeight(child.weight || 0)
						.setValue(child.value)
						.setFlags(flags)
						.setPeOrder(get(child, 'peOrder') !== -1 ? get(child, 'peOrder') : null) // if value is -1 (default in AC Server) set to null
						.setTpmId(child.tpmId)
						.build();
				} else { // Security
					const security: Security = new SecurityDirector().constructByParams(
						child.allData?.customClientProperties?.description || child.allData?.description || child.description,
						child.identifier,
						child.identifierType,
						null,
						(child?.allData?.ticker || child?.ticker) ?? '',
						!hasErrors ? (get(child, 'allData.funds.morningstarSecId.stringValue') || child.morningstarSecId) : '', // If this holding has errors this field needs to be empty for error handler to recognise invalid security
						!hasErrors ? (child?.allData?.globalId || child?.globalId) ?? '' : '', // If this holding has errors this field needs to be empty for error handler to recognise invalid security
						!hasErrors ? child?.allData?.assetId ?? child?.allData?.aladdinId ?? child?.assetId ?? child?.aladdinId : '', // If this holding has errors this field needs to be empty for error handler to recognise invalid security
						!hasErrors ? child?.allData?.cusip ?? child?.cusip : '', // persisting for WDS cash securities
						get(child, 'allData.market'),
						get(child, 'allData.currencyCode'),
						child?.allData?.isin ?? child?.isin,
						child.weight || 0,
						child.value,
						null,
						Object.keys(get(child, 'allData.assetBreakdowns.acAssetClass.values', {}))?.[0] ?? child.allData?.assetClass,
						child?.allData?.assetType,
						get(child, 'allData.funds.morningstarCategory.stringValue', get(child, 'morningstarCategory')),
						get(child, 'allData.funds.morningstarFundId.stringValue', child.morningstarFundId),
						get(child, 'allData.otherIdentifiers', child.otherIdentifiers), // needs to be persisted for partner securities
						get(child, 'allData.substitutedFrom', child.substitutedFrom), // needs to be persisted for partner securities that are substitutions
						get(child, 'peOrder') !== -1 ? get(child, 'peOrder') : null, // if value is -1 (default in AC Server) set to null
						child.allData?.aladdinPortfolioCode // needed for partner portfolios with model securities
					);

					if (hasErrors) {
						security.isUnresolved = true;
					}

					return security;
				}
			});

			// * 2: Create PortfolioChild
			node = {
				id: portfolioDataId,
				uniqueId: get(portfolioData, 'uniqueId'),
				description: PortfolioEditorViewTreeReducerUtil.mapDescription(portfolioData, modelsConfig),
				type: portfolioData.type,
				subType: portfolioData.subType,
				baseModel: get(portfolioData, 'allData.baseModel', portfolioData.baseModel), // needs to be persisted for partner customization
				baseModelType: get(portfolioData, 'allData.baseModelType', portfolioData.baseModelType), // needs to be persisted for partner customization
				baseModelWasDirty: get(portfolioData, 'allData.baseModelWasDirty', portfolioData.baseModelWasDirty), // needs to be persisted for partner customization
				childrenRef: childRefArray,
				parents: compact([parent]),
				isFlat: !portfolioData.children.some(child => child.subType === PortfolioType.USER || child.portfolio?.type === PortfolioType.MODEL) || every(portfolioData.children, ['identifierType', PortfolioType.MODEL]), // only when a model portfolio is added in the editor
				flags: get(portfolioData, 'allData.flags'),
				originPlatform: get(portfolioData, 'originPlatform'),
				otherIdentifiers: get(portfolioData, 'otherIdentifiers'),
				dateLastModified: get(portfolioData, 'allData.dateLastModified', portfolioData.dateLastModified),
				originNotTradableSecurities: uniq(get(portfolioData, 'allData.originNotTradableSecurities', portfolioData.originNotTradableSecurities)),
				country: get(portfolioData, 'allData.country'),
				tags: portfolioData?.allData?.tags ?? [],
				aladdinPortfolioCode: portfolioData.aladdinPortfolioCode,
				tpmId: portfolioData.tpmId
			} as PortfolioChild;

			// * 3: Check if PortfolioChild already exists in the portfolioChildMap
			if (portfolioChildMap[portfolioDataId]) {
				// any risk in overwriting a PortfolioChild by checking for the id? Data should be identical.
				const oldPortfolioChildParents = portfolioChildMap[portfolioDataId].parents || [];
				node.parents = uniq([...oldPortfolioChildParents, ...node.parents]);
			}

			// * 4: Add to the map - if it already exists, new object will win out
			portfolioChildMap = {
				...portfolioChildMap,
				[portfolioDataId]: node
			};

			// * 5: Keep traversing down the tree and return the updated map
			portfolioChildMap = portfolioData.children.reduce((accPortfolioChildMap: TreeNodeMap, currentValue) => {
				return this.buildPortfolioChildNode(currentValue, portfolioDataId, accPortfolioChildMap, modelsConfig);
			}, portfolioChildMap);
		}
		return portfolioChildMap;
	}

	public static addRefToParent(
		portfolioChildMap: TreeNodeMap,
		parentId: string,
		id: string,
		type: string,
		subType: string,
		baseModel: string,
		description: string,
		aladdinPortfolioCode: string,
		tpmId?: string,
		portfolioAttribute?: BasicPortfolioAttributes
	): TreeNodeMap {
		const parentToPortRef = new PortfolioReferenceBuilder()
			.setIdentifier(id)
			.setDescription(description)
			.setType(type)
			.setSubType(subType)
			.setBaseModel(baseModel)
			.setAladdinPortfolioCode(aladdinPortfolioCode)
			.setTpmId(tpmId)
			.setWeight(get(portfolioAttribute, 'weight', null))
			.setValue(get(portfolioAttribute, 'value', 0))
			.build();

		if (PortfolioIdUtil.isBlackRockModel(id)) {
			parentToPortRef.identifierType = WebUserDataConstants.IDENTIFIER_TYPE.MODEL_ID;
		} else if (PortfolioIdUtil.isThirdPartyModel(id)) {
			parentToPortRef.identifierType = WebUserDataConstants.IDENTIFIER_TYPE.THIRD_PARTY_ID;
		}

		return {
			...portfolioChildMap,
			[parentId]: {
				...portfolioChildMap[parentId],
				childrenRef: [parentToPortRef, ...portfolioChildMap[parentId].childrenRef]
			}
		};
	}

	// Remove the portfolio with id=id from portfolioChildMap[parentId]
	public static deleteRefFromParent(portfolioChildMap: TreeNodeMap, parentId: string, id: string, compareAladdinId: boolean = false): TreeNodeMap {
		return {
			...portfolioChildMap,
			[parentId]: {
				...portfolioChildMap[parentId],
				childrenRef: [...portfolioChildMap[parentId].childrenRef].filter(child => compareAladdinId && child.aladdinId ? child.aladdinId !== id : child.identifier !== id)
			}
		};
	}

	public static addImpactedPortfolios(portfolioChildMap: TreeNodeMap, parentId: string, impactedPortfolios: Array<any>): TreeNodeMap {
		const newPortfolioChild: PortfolioChild = {
			...portfolioChildMap[parentId],
			impactedPortfolios: impactedPortfolios
		};
		return {
			...portfolioChildMap,
			[parentId]: newPortfolioChild
		};
	}

	public static mapDescription(portfolioData: any, modelsConfig: Array<any>): string {
		const id = get(portfolioData, 'id') || get(portfolioData, 'portfolio.id') || get(portfolioData, 'uniqueId');
		// portfolio.description contains the updated portfolio name from WebUserData, if we modify the child portfolio after its creation
		const description = get(portfolioData, 'portfolio.description') || get(portfolioData, 'description');

		// To check case Blackrock model inside a portfolio, I have to also check inside the portfolio attribute
		if (PortfolioIdUtil.isModel(id)) {
			const model = modelsConfig.find(port => id === port.id);
			const modelName = model?.name || description;
			return ModelNameUtil.getModelExternalName(model, modelName);
		}

		return description;
	}

	/**
	 * To show inline alerts in overview page, persisting substituteFrom Object in WTP
	 *
	 * @static
	 * @param {*} security - incoming security
	 * @param {*} updatedSecurity - existing security
	 * @memberof PortfolioEditorViewTreeReducerUtil
	 */
	private static updateSubstituteFromObject(security, updatedSecurity): void {

		// incoming security
		const substitutedFrom: SubstitutedFrom = get(security, 'substitutedFrom');

		if (substitutedFrom) {
			// incoming security
			const substitutionsFromInvalidSecurities = get(security, 'substitutedFrom.invalidSecurities', []);
			const substitutionsFromAssetClass = get(security, 'substitutedFrom.assetClass', []);

			// existing security
			const updatedSubstitutedFrom = get(updatedSecurity, 'substitutedFrom');
			const updatedSubstitutionsFromInvalidSecurities = get(updatedSubstitutedFrom, 'invalidSecurities', []);
			const updatedSubstitutionsFromAssetClass = get(updatedSubstitutedFrom, 'assetClass', []);

			// Existing security substitutions should be merged with any incoming substitutions
			// doing the concatenation for arrays, concatenate any incoming and existing substitutions.
			updatedSecurity.substitutedFrom = {
				invalidSecurities: [...updatedSubstitutionsFromInvalidSecurities, ...substitutionsFromInvalidSecurities],
				assetClass: [...updatedSubstitutionsFromAssetClass, ...substitutionsFromAssetClass]
			};
		}
	}

	// TODO I think we can delete. Now if store has old ids we use old ids. if store has new ids we use new ids
	/**
	 * We need to add translation to load newly created blend in old infra, TODO wdsDecom: can be removed once all tools are fully migrated on new infra
	 */
	// public static setCompatibleKey(modelsConfig, portfolioDataId, shouldUseDasPortfolioId) {
	// 	return PortfolioIdUtil.isBlackRockModel(portfolioDataId) && shouldUseDasPortfolioId ?
	// 		modelsConfig.find(port => port?.id === portfolioDataId)?.dasPortfolioId : portfolioDataId;
	// }
}
